import * as React from "react"
import { cn } from "@shadcn/lib/utils"
import { CompactPageHeader, type CompactPageHeaderProps } from "@shadcn/components/layouts/headers/CompactPageHeader"
import {
	UnderlinedTabs,
	UnderlinedTabsList,
	UnderlinedTabsTrigger,
	UnderlinedTabsContent,
} from "@shadcn/components/molecules/UnderlinedTabs"
import type { LucideIcon } from "lucide-react"

/**
 * Individual tab configuration
 */
export interface TabConfig {
	/**
	 * Tab identifier
	 */
	value: string
	/**
	 * Tab label
	 */
	label: string
	/**
	 * Content for this tab
	 */
	content: React.ReactNode
	/**
	 * Optional icon component
	 */
	icon?: LucideIcon
}

/**
 * Tabs configuration for TabbedPage
 */
export interface TabbedPageTabsProps {
	/**
	 * Tab configurations
	 */
	items: TabConfig[]
	/**
	 * Current active tab (controlled)
	 */
	activeTab?: string
	/**
	 * Callback when tab changes
	 */
	onTabChange?: (value: string) => void
}

/**
 * Props for TabbedPage component
 */
export interface TabbedPageProps {
	/**
	 * Header configuration (icon, title, actions)
	 * Uses the same props as CompactPageHeader
	 */
	header: CompactPageHeaderProps
	/**
	 * Tabs configuration (items, active tab, change handler)
	 */
	tabs: TabbedPageTabsProps
	/**
	 * Additional class name
	 */
	className?: string
}

/**
 * TabbedPage - A page layout with compact header and underlined tabs
 * Used for pages that need multiple tab views with flexible children (can be tables, forms, or any content)
 *
 * @example Basic usage
 * ```tsx
 * <TabbedPage
 *   header={{
 *     icon: Sparkles,
 *     title: "AI Builder",
 *     actions: <Button>Settings</Button>
 *   }}
 *   tabs={{
 *     items: [
 *       { value: "custom", label: "Custom", content: <CustomTab /> },
 *       { value: "default", label: "Default", content: <DefaultTab /> }
 *     ],
 *     activeTab: activeTab,
 *     onTabChange: setActiveTab
 *   }}
 * />
 * ```
 */
export function TabbedPage({ header, tabs, className }: Readonly<TabbedPageProps>) {
	const [internalActiveTab, setInternalActiveTab] = React.useState(tabs.items[0]?.value || "")

	const currentTab = tabs.activeTab ?? internalActiveTab

	const handleTabChange = (value: string) => {
		setInternalActiveTab(value)
		tabs.onTabChange?.(value)
	}

	return (
		<div className={cn("flex flex-1 flex-col gap-2 p-4 xl:p-8", className)}>
			{/* Compact Header */}
			<CompactPageHeader {...header} />

			{/* Tabs with Underline */}
			<UnderlinedTabs value={currentTab} onValueChange={handleTabChange}>
				<UnderlinedTabsList className="[&>*:first-child]:pl-9">
					{tabs.items.map((tab) => {
						const Icon = tab.icon
						return (
							<UnderlinedTabsTrigger key={tab.value} value={tab.value}>
								{Icon && <Icon className="h-4 w-4 mr-2" />}
								{tab.label}
							</UnderlinedTabsTrigger>
						)
					})}
				</UnderlinedTabsList>

				{tabs.items.map((tab) => (
					<UnderlinedTabsContent key={tab.value} value={tab.value} className="mt-4">
						{tab.content}
					</UnderlinedTabsContent>
				))}
			</UnderlinedTabs>
		</div>
	)
}
